import { Reveal, SectionLabel } from "../Reveal";
import { MUSIC } from "../../data/content";
import { Music2, ArrowUpRight } from "lucide-react";

export const Music = () => (
  <section id="music" className="relative py-24 sm:py-32 md:py-40 bg-noise border-t border-white/10 overflow-hidden" data-testid="music-section">
    {/* neon glow */}
    <div className="absolute top-1/4 -left-40 w-96 h-96 rounded-full bg-[#6735FA] opacity-20 blur-[120px] pointer-events-none" />
    <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-[#22AED4] opacity-20 blur-[120px] pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
      <SectionLabel color="#6735FA">{MUSIC.label}</SectionLabel>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-[0.95]">
            {MUSIC.heading}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-2">
            {MUSIC.genres.map((g) => (
              <span key={g} className="font-display text-xs uppercase tracking-widest border border-[#6735FA]/40 px-4 py-2 text-white/70">{g}</span>
            ))}
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.05}>
        <div className="relative overflow-hidden mb-6 border border-white/10 group">
          <img
            src="https://customer-assets.emergentagent.com/job_racer-beats-pro/artifacts/7gtc8f1k_IMG_9089.jpg"
            alt="REYCRO in the studio"
            className="w-full aspect-[21/9] object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <p className="absolute bottom-5 left-6 font-display text-sm uppercase tracking-widest text-white/90">In the studio</p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* services */}
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {MUSIC.services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <a
                href={s.href}
                target={s.external ? "_blank" : undefined}
                rel={s.external ? "noopener noreferrer" : undefined}
                data-testid={`music-service-${i}`}
                className="block border border-white/10 bg-[#0A0A0A] p-7 h-full group hover:border-[#6735FA]/50 hover:bg-[#6735FA]/5 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <Music2 size={22} className="text-[#22AED4] mb-4" />
                  <ArrowUpRight size={18} className="text-white/30 group-hover:text-[#6735FA] transition-colors" />
                </div>
                <h3 className="font-display text-lg font-semibold uppercase tracking-wide text-white mb-2">{s.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{s.desc}</p>
              </a>
            </Reveal>
          ))}
        </div>

        {/* embed placeholder + catalog */}
        <div className="lg:col-span-5 flex flex-col gap-6">
          <Reveal delay={0.08}>
            <div className="relative overflow-hidden border border-[#1DB954]/30 bg-black" data-testid="spotify-embed">
              <iframe
                title="REYCRO on Spotify"
                style={{ borderRadius: 0 }}
                src={`https://open.spotify.com/embed/artist/${MUSIC.spotifyArtistId}?utm_source=generator&theme=0`}
                width="100%"
                height="352"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative overflow-hidden border border-[#6735FA]/30 bg-black" data-testid="soundcloud-embed">
              <iframe
                title="REYCRO on SoundCloud"
                width="100%"
                height="400"
                scrolling="no"
                frameBorder="no"
                allow="autoplay"
                src="https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/reycro&color=%236735FA&auto_play=false&hide_related=false&show_comments=false&show_user=true&show_reposts=false&show_teaser=true&visual=true"
              />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <a
              href={MUSIC.beatstarsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="beatstars-cta"
              className="group flex items-center justify-between border border-[#BAF173]/40 bg-[#BAF173]/[0.06] hover:bg-[#BAF173]/10 px-6 py-5 transition-colors"
            >
              <div>
                <p className="font-display text-sm uppercase tracking-widest text-[#BAF173]">Buy Beats on BeatStars</p>
                <p className="text-xs text-white/50 mt-1">Original beats &amp; leasing · drill, trap, reggaeton, EDM</p>
              </div>
              <ArrowUpRight size={26} className="text-[#BAF173] shrink-0 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>

          <Reveal delay={0.18}>
            <a
              href={MUSIC.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="youtube-cta"
              className="group flex items-center justify-between border border-[#FF0000]/40 bg-[#FF0000]/[0.06] hover:bg-[#FF0000]/10 px-6 py-5 transition-colors"
            >
              <div>
                <p className="font-display text-sm uppercase tracking-widest text-[#FF3B3B]">Watch on YouTube</p>
                <p className="text-xs text-white/50 mt-1">Videos, sessions &amp; releases · @reycro</p>
              </div>
              <ArrowUpRight size={26} className="text-[#FF3B3B] shrink-0 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="border border-white/10 bg-[#0A0A0A] p-6">
              <h3 className="font-display text-sm uppercase tracking-widest text-white/50 mb-4">Selected Catalog · 12+ Beats</h3>
              <ul className="space-y-2.5">
                {MUSIC.catalog.map((t, i) => (
                  <li key={t} className="flex items-center gap-3 group" data-testid="catalog-track">
                    <span className="font-display text-xs text-white/30 w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-white/75 group-hover:text-[#22AED4] transition-colors">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <span className="font-display text-xs uppercase tracking-widest text-white/40">Stream on</span>
          {MUSIC.platforms.map((p) =>
            p.href ? (
              <a key={p.name} href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`platform-${p.name.toLowerCase()}`} className="font-display text-xs uppercase tracking-widest border border-white/15 px-4 py-2 text-white/70 hover:border-[#22AED4] hover:text-white transition-colors">{p.name}</a>
            ) : (
              <span key={p.name} className="font-display text-xs uppercase tracking-widest border border-white/10 px-4 py-2 text-white/40 cursor-default">{p.name}</span>
            )
          )}
        </div>
      </Reveal>
    </div>
  </section>
);
